import { useState, useRef, MouseEvent, TouchEvent, memo } from "react";
import { Link } from "react-router-dom";
import { MapPin, ShieldCheck, ArrowUp, Flame, Camera, Navigation, Sparkles, Clock, ChevronLeft, ChevronRight, Gem, MessageCircle, Zap, Trophy, Star } from "lucide-react";
import { haversineKm } from "@/lib/geo";
import { formatPriceDisplay, formatPriceRange } from "@/lib/price";
import { isNewListing, formatLastActivityBadge, buildListingPath, type ListingLike } from "@/lib/listings";
import FavoriteButton from "@/components/FavoriteButton";

type ServiceItem = { title: string; highlight?: boolean };

export default memo(function ProfessionalCard({ listing, idx = 0, userLocation = null }: { listing: ListingLike; idx?: number; userLocation?: { lat: number; lng: number } | null }) {
  const [photoIdx, setPhotoIdx] = useState(0);
  const [parallax, setParallax] = useState({ x: 0, y: 0 });
  const touchStart = useRef<number | null>(null);

  const photos = listing.photos && listing.photos.length > 0
    ? listing.photos
    : [listing.cover_photo || "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=800"];

  const currentPhoto = photos[photoIdx];
  const photoCount = photos.length;

  const featuredRemaining =
    typeof listing.featured_remaining_minutes === "number" ? listing.featured_remaining_minutes : 0;
  const isFeatured = !!listing.featured || featuredRemaining > 0;
  const isVerified = !!listing.user_verified;
  // Badges
  const isFastResponse = typeof listing.response_time_avg_min === "number" && listing.response_time_avg_min <= 15;
  const isTopAdvertiser = typeof listing.top_score === "number" && listing.top_score >= 0.7;
  // Reviews
  const avgStars = typeof listing.avg_stars === "number" ? listing.avg_stars : null;
  const totalReviews = typeof listing.total_reviews === "number" ? listing.total_reviews : 0;
  const hasReviews = totalReviews > 0 && avgStars !== null;
  const adTitle = (listing.name || "").trim();
  const displayName = (listing.user_display_name || "").trim() || adTitle;
  const waRaw = (listing.whatsapp || listing.phone || "").replace(/\D/g, "");
  const waUrl = waRaw
    ? `https://wa.me/${waRaw}?text=${encodeURIComponent(`Olá ${displayName || ""}! Vi seu perfil no Forfada e gostaria de saber mais.`)}`
    : null;
  const boostRemaining =
    typeof listing.bumped_remaining_minutes === "number" ? listing.bumped_remaining_minutes : 0;
  const isBoostActive = boostRemaining > 0;
  const isNew = isNewListing(listing);
const highlightedServices = Array.isArray(listing.services)
    ? listing.services.filter((s: ServiceItem) => s && s.title && s.highlight).slice(0, 5)
    : [];
  const price = formatPriceDisplay(listing.starting_price, listing.price_max);
  const priceRanges = Array.isArray(listing.price_ranges) ? listing.price_ranges.filter((pr) => pr && (pr.label || pr.value)) : [];
  const activitySource = listing.user_last_active_at || listing.updated_at || listing.created_at;
  const activityLabel = formatLastActivityBadge(activitySource);

  const shortBio = (listing.short_bio || "").trim();
  const age = typeof listing.user_age === "number" && listing.user_age > 0 ? listing.user_age : null;
  const headline = age ? `${displayName}, ${age}` : displayName;

  let distanceLabel = null;
  const distanceKm =
    listing.distance_km != null
      ? listing.distance_km
      : userLocation && listing.lat != null && listing.lng != null
        ? haversineKm(userLocation, { lat: listing.lat, lng: listing.lng })
        : null;
  if (distanceKm != null) {
    distanceLabel = distanceKm < 1 ? `${Math.round(distanceKm * 1000)} m` : `${distanceKm.toFixed(1)} km`;
  }

  const showLocation = listing.show_city && (listing.city || listing.neighborhood);
  const locationText = showLocation
    ? `${listing.neighborhood ? `${listing.neighborhood}, ` : ""}${listing.city || ""}${listing.state ? ` · ${listing.state}` : ""}`.trim()
    : "";

  const handleMouseMove = (e: MouseEvent<HTMLAnchorElement>) => {
    if (window.matchMedia('(max-width: 1023px)').matches) return;
    const { left, top, width, height } = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - left) / width - 0.5) * 15;
    const y = ((e.clientY - top) / height - 0.5) * 15;
    setParallax({ x, y });
  };

  const handleMouseLeave = () => {
    setParallax({ x: 0, y: 0 });
  };

  const nextPhoto = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setPhotoIdx((p) => (p + 1) % photoCount);
  };

  const prevPhoto = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setPhotoIdx((p) => (p - 1 + photoCount) % photoCount);
  };

  const handleTouchStart = (e: TouchEvent<HTMLDivElement>) => {
    touchStart.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: TouchEvent<HTMLDivElement>) => {
    if (!touchStart.current) return;
    const touchEnd = e.changedTouches[0].clientX;
    const diff = touchStart.current - touchEnd;
    if (Math.abs(diff) > 40) {
      if (diff > 0) nextPhoto(e as unknown as MouseEvent<HTMLButtonElement>);
      else prevPhoto(e as unknown as MouseEvent<HTMLButtonElement>);
    }
    touchStart.current = null;
  };

  return (
    <div className="pro-card-wrapper relative group" data-testid={`acompanhante-card-${idx}`}>
      <Link
        to={buildListingPath(listing) || `/acompanhante/${listing.id}`}
        className={`pro-card reveal ${isFeatured ? "is-featured" : ""}`}
        style={{ animationDelay: `${Math.min(idx, 6) * 0.05}s` }}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        {!listing.is_online && activityLabel && (
          <span
            className="pro-card-activity-badge absolute left-1/2 z-30"
            title="Última atividade do acompanhante"
            data-testid={`activity-badge-${idx}`}
          >
            <Clock className="w-3 h-3" />
            {activityLabel}
          </span>
        )}
      <div
        className="photo-wrap"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {isFeatured && (
          <div className="pro-card-featured-badge">
            <span
              className="pointer-events-auto inline-flex items-center gap-1.5 text-[11px] font-extrabold uppercase tracking-[0.14em] px-3 py-1.5 rounded-full text-white shadow-heat-glow shrink-0 ring-1 ring-white/25"
              style={{ background: "linear-gradient(135deg, rgb(225 29 72) 0%, rgb(236 72 153) 100%)" }}
              data-testid={`featured-badge-${idx}`}
            >
              <Flame className="w-3.5 h-3.5 fill-current" /> Em destaque
            </span>
          </div>
        )}

        <img
          src={currentPhoto}
          alt={displayName}
          loading="lazy"
          className="pro-card-image"
          style={{
            transform: `scale(1.12) translate(${parallax.x}px, ${parallax.y}px)`,
            transition: (parallax.x === 0 && parallax.y === 0) ? "transform 0.5s cubic-bezier(0.16, 1, 0.3, 1)" : "none"
          }}
        />

        {/* Carousel Arrows (only if more than 1 photo) */}
        {photoCount > 1 && (
          <>
            <button
              onClick={prevPhoto}
              className={`absolute left-2 top-1/2 -translate-y-1/2 z-20 w-8 h-8 flex items-center justify-center rounded-full bg-black/30 backdrop-blur-md text-white border border-white/20 transition-opacity hover:bg-black/50 ${isFeatured ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
              aria-label="Foto anterior"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={nextPhoto}
              className={`absolute right-2 top-1/2 -translate-y-1/2 z-20 w-8 h-8 flex items-center justify-center rounded-full bg-black/30 backdrop-blur-md text-white border border-white/20 transition-opacity hover:bg-black/50 ${isFeatured ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
              aria-label="Próxima foto"
            >
              <ChevronRight className="w-5 h-5" />
            </button>

            {/* Dots / Indicator */}
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 flex gap-1">
              {photos.map((_, i: number) => (
                <div
                  key={i}
                  className={`w-1 h-1 rounded-full transition-all ${i === photoIdx ? "bg-white w-3" : "bg-white/40"}`}
                />
              ))}
            </div>
          </>
        )}

        <div className={`absolute ${isFeatured ? "top-12" : "top-3"} left-3 flex flex-col items-start gap-1.5 z-10`}>
            <FavoriteButton listingId={listing.id!} data-testid={`favorite-btn-${idx}`} />
            {isFastResponse && (
              <span className="inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-[0.14em] px-2.5 py-1 rounded-full bg-amber-500 text-white shadow-sm ring-1 ring-white/30">
                <Zap className="w-3 h-3" /> Resposta rápida
              </span>
            )}
            {isTopAdvertiser && (
              <span className="inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-[0.14em] px-2.5 py-1 rounded-full bg-yellow-500 text-white shadow-sm ring-1 ring-white/30">
                <Trophy className="w-3 h-3" /> Top Anunciante
              </span>
            )}
          {listing.is_online && (
            <span className="badge bg-white/95 text-emerald-700 border-emerald-200/80">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
              </span>
              Online
            </span>
          )}
        </div>

        <div className={`absolute ${isFeatured ? "top-12" : "top-3"} right-3 flex flex-col items-end gap-1.5 z-10`}>
          {isNew && !isFeatured && (
            <span
              className="badge bg-emerald-500 text-white border-transparent"
              title="Perfil Novo"
              data-testid={`new-badge-${idx}`}
            >
              <Sparkles className="w-3 h-3" />
            </span>
          )}
          {isBoostActive && !isFeatured && (
            <span
              className="badge bg-ink-900/85 text-cream-50 border-transparent"
              title={`No topo há ${boostRemaining} min`}
            >
              <ArrowUp className="w-3 h-3" />
            </span>
          )}
          {photoCount > 1 && (
            <span className="badge bg-ink-900/85 text-cream-50 border-transparent">
              <Camera className="w-3 h-3" /> {photoCount}
            </span>
          )}
        </div>

        {isFeatured ? (
          <div className="pro-card-featured-info">
            <h3 className="pro-card-featured-info__title">
              <span className="line-clamp-1 min-w-0">{headline}</span>
              {isVerified && (
                <span
                  className="pro-card-verified-dot"
                  data-testid={`verified-badge-${idx}`}
                  aria-label="Perfil verificado"
                >
                  <ShieldCheck className="w-3 h-3" aria-hidden="true" />
                  <span className="pro-card-verified-tooltip" role="tooltip" aria-hidden="true">
                    Perfil Verificado
                  </span>
                </span>
              )}
            </h3>
            {hasReviews && (
              <div className="flex items-center gap-1.5 text-sm text-white/80 mt-1">
                <Star className="w-3.5 h-3.5 fill-current text-amber-400" />
                <span className="font-bold">{avgStars.toFixed(1)}</span>
                <span className="text-white/60">·</span>
                <span>{totalReviews} avalia{totalReviews !== 1 ? "ções" : "ção"}</span>
              </div>
            )}
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {isFastResponse && (
                <span className="inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-[0.14em] px-2.5 py-1 rounded-full bg-amber-500 text-white shadow-sm ring-1 ring-white/30">
                  <Zap className="w-3 h-3" /> Resposta rápida
                </span>
              )}
              {isTopAdvertiser && (
                <span className="inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-[0.14em] px-2.5 py-1 rounded-full bg-yellow-500 text-white shadow-sm ring-1 ring-white/30">
                  <Trophy className="w-3 h-3" /> Top Anunciante
                </span>
              )}
            </div>
            {shortBio && (
              <p
                className="text-white/80 text-sm leading-snug line-clamp-2 mt-1"
                data-testid={`ad-title-${idx}`}
              >
                {shortBio}
              </p>
            )}
            {highlightedServices.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {highlightedServices.map((s: ServiceItem, i: number) => (
                  <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-white/10 text-white border border-white/20">
                    {s.title}
                  </span>
                ))}
              </div>
            )}

            {(showLocation || distanceLabel) && (
              <div className="pro-card-featured-info__row">
                {showLocation && (
                  <span className="pro-card-featured-info__location" title={locationText}>
                    <MapPin className="w-3.5 h-3.5 shrink-0" />
                    <span className="line-clamp-1 min-w-0">{locationText}</span>
                  </span>
                )}
                {distanceLabel && (
                  <span
                    className="pro-card-featured-info__distance"
                    title="Distância aproximada de você"
                    data-testid={`distance-badge-${idx}`}
                  >
                    <Navigation className="w-3 h-3" />
                    {distanceLabel}
                  </span>
                )}
              </div>
            )}

            {price && (
              <div className="pro-card-featured-info__price mt-1">
                <Gem className="w-4 h-4 text-white/70" />
                <span className="font-bold">{price.value}</span>
                {price.kind === "range" && price.label && (
                  <span className="text-xs text-white/60 ml-1">{price.label}</span>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="pro-card-photo-name">
            <h3 className="pro-card-photo-name__title">
              <span className="line-clamp-1 min-w-0">{headline}</span>
              {isVerified && (
                <span
                  className="pro-card-verified-dot"
                  data-testid={`verified-badge-${idx}`}
                  aria-label="Perfil verificado"
                >
                  <ShieldCheck className="w-3 h-3" aria-hidden="true" />
                  <span className="pro-card-verified-tooltip" role="tooltip" aria-hidden="true">
                    Perfil Verificado
                  </span>
                </span>
              )}
            </h3>
            {hasReviews && (
              <div className="flex items-center gap-1.5 text-sm text-ink-600 mt-1">
                <Star className="w-3.5 h-3.5 fill-current text-amber-400" />
                <span className="font-bold">{avgStars.toFixed(1)}</span>
                <span className="text-ink-400">·</span>
                <span>{totalReviews} avalia{totalReviews !== 1 ? "ções" : "ção"}</span>
              </div>
            )}
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {isFastResponse && (
                <span className="inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-[0.14em] px-2.5 py-1 rounded-full bg-amber-500 text-white shadow-sm ring-1 ring-white/30">
                  <Zap className="w-3 h-3" /> Resposta rápida
                </span>
              )}
              {isTopAdvertiser && (
                <span className="inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-[0.14em] px-2.5 py-1 rounded-full bg-yellow-500 text-white shadow-sm ring-1 ring-white/30">
                  <Trophy className="w-3 h-3" /> Top Anunciante
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {!isFeatured && (
        <div className="pro-card-body">
          {(showLocation || distanceLabel) && (
            <div className="pro-card-row pro-card-row--location">
              {showLocation && (
                <span className="pro-card-location" title={locationText}>
                  <MapPin className="w-3.5 h-3.5 shrink-0 text-heat-600" />
                  <span className="line-clamp-1 min-w-0">{locationText}</span>
                </span>
              )}
              {distanceLabel && (
                <span
                  className="pro-card-distance"
                  title="Distância aproximada de você"
                  data-testid={`distance-badge-${idx}`}
                >
                  <Navigation className="w-3 h-3" />
                  {distanceLabel}
                </span>
              )}
            </div>
          )}

            {shortBio && (
              <p className="pro-card-bio" data-testid={`ad-title-${idx}`}>{shortBio}</p>
            )}
            {listing.description && listing.description !== shortBio && (
              <p className="pro-card-bio" data-testid={`ad-description-${idx}`}>{listing.description}</p>
            )}

          {highlightedServices.length > 0 && (
            <div className="pro-card-services">
              {highlightedServices.map((s: ServiceItem, i: number) => (
                <span key={i} className="pro-card-service-pill">
                  {s.title}
                </span>
              ))}
            </div>
          )}

          <div className="pro-card-footer">
            {priceRanges.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {priceRanges.map((pr, i) => (
                  <span key={i} className="text-xs font-semibold px-2 py-1 rounded-md bg-cream-100 border border-cream-200">
                    {pr.label && <span className="text-ink-700">{pr.label}</span>}
                    {pr.label && pr.value && <span className="text-ink-400 mx-1">·</span>}
                    {pr.value && <span className="text-heat-600">{formatPriceRange(pr.value)}</span>}
                  </span>
                ))}
              </div>
            ) : (
              <div className="pro-card-price">
                <span className="pro-card-price__label">{price ? price.label : "Valor"}</span>
                <span
                  className={`pro-card-price__value ${
                    price && price.kind === "range" ? "pro-card-price__value--range" : ""
                  }`}
                >
                  {price ? price.value : "Sob consulta"}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
      </Link>
      {waUrl && (
        <a
          href={waUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => { e.stopPropagation(); }}
          data-testid={`wa-fab-${idx}`}
          aria-label="Conversar no WhatsApp"
          className="absolute z-30 bottom-3 right-3 flex items-center gap-1.5 rounded-full bg-[#25D366] text-white text-xs font-bold px-3 py-2 shadow-lg ring-1 ring-white/30 md:opacity-0 md:group-hover:opacity-100 transition-opacity"
        >
          <MessageCircle className="w-4 h-4" /> WhatsApp
        </a>
      )}
    </div>
  );
});
